/**
 * aboAccess.ts — Zentrale Abo-Logik und Zugriffsprüfungen.
 *
 * Alle Funktionen rund um Abo-Status, Probezeit und Marktplatz-Zugang
 * sind hier gebündelt (Single Responsibility).
 */

import { PROBE_MONATE } from '../config/constants.js';
import { MS_PER_DAY, dateToLocal } from './format.js';

/**
 * Input-Shape für alle Abo-Funktionen — nur die Felder, die wirklich
 * gelesen werden. Echte `Profile`-Objekte sind strukturell kompatibel
 * (duck typing), daher wird dieser Typ nicht exportiert.
 */
interface AboUser {
  aboAktiv?: boolean;
  aboEndetAm?: string | null;
  aboPausiert?: boolean;
  aboPausiertBis?: string | null;
  gekuendigtAm?: string | null;
  aboStartAm?: string | null;
  probeStart?: string | null;
  createdAt?: string | null;
  registriertAm?: string | null;
  kycStatus?: string;
  aboStripeStatus?: string | null;
}

/**
 * Prüft ob ein User ein aktives (nicht pausiertes, nicht abgelaufenes) Abo hat.
 *
 * Ein gekündigtes Abo läuft bis EINSCHLIESSLICH `aboEndetAm` (Tag zählt
 * ganz, wie in der Datenbank: `hat_laufendes_abo`, Migration 145). Vorher
 * endete es hier um 00:00 Uhr UTC des Endtags.
 */
export function hatAbo(u: AboUser | null | undefined): boolean {
  if (!u) return false;
  if (u.aboPausiert && u.aboPausiertBis && new Date(u.aboPausiertBis) >= new Date()) return false;
  if (u.aboAktiv === true) return true;
  if (u.aboEndetAm && String(u.aboEndetAm).slice(0, 10) >= dateToLocal(new Date())) return true;
  return false;
}

/**
 * Hatte das Konto schon einmal ein Abo? Merkmal: `aboStartAm` ist gesetzt
 * (Stripe-Webhook und Admin-Freischaltung setzen es). Wer schon ein Abo
 * hatte, bekommt keine Gratisphase mehr — auch nicht nach einer Kündigung
 * oder Admin-Verlängerung (Entscheidung Sertan 18.10.2026, Migration 145).
 */
export function hatteSchonAbo(u: AboUser | null | undefined): boolean {
  return !!u && !!u.aboStartAm && String(u.aboStartAm).trim() !== '';
}

/**
 * Stripe-Abo in der Gratisphase: gebucht, Zahlung hinterlegt, abgebucht wird
 * erst ab Ende der Gratisphase (Stripe-Status „trialing", Migration 147).
 * Zugang hat der Kunde ({@link hatAbo}), zahlend ist er noch nicht.
 */
export function inStripeGratisphase(u: AboUser | null | undefined): boolean {
  return !!u && u.aboStripeStatus === 'trialing' && hatAbo(u);
}

/**
 * Zahlender Kunde: laufendes Abo, aber nicht in der Stripe-Gratisphase.
 * Für Admin-Zahlen („Aktive Abos", Monatsumsatz) — Entscheidung Sertan
 * 19.10.2026: Gratisphase mit Stripe getrennt zählen.
 */
export function zahltAbo(u: AboUser | null | undefined): boolean {
  return hatAbo(u) && !inStripeGratisphase(u);
}

/**
 * Prüft ob das Abo eines Users aktuell pausiert ist.
 */
export function aboIstPausiert(u: AboUser | null | undefined): boolean {
  if (!u || !u.aboPausiert || !u.aboPausiertBis) return false;
  return new Date(u.aboPausiertBis) >= new Date();
}

/**
 * Prüft ob ein User gekündigt hat, aber das Abo noch bis zum Periodenende läuft.
 */
export function aboGekuendigt(u: AboUser | null | undefined): boolean {
  if (!u || !u.gekuendigtAm) return false;
  return hatAbo(u);
}

/**
 * Ganze Kalendertage von heute bis zum Abo-Ende. Der Endtag zählt ganz
 * (Migration 145): Endet das Abo heute → 0 („endet heute"), morgen → 1.
 * Vorher wurde ab 00:00 Uhr UTC des Endtags gerechnet — am Endtag selbst
 * stand „0 Tage verbleibend", obwohl der Tag noch lief (3b-2, 19.10.2026).
 *
 * @param jetzt Bezugszeitpunkt (für Tests)
 * @returns Tage (0 ohne Enddatum, am Endtag oder danach).
 */
export function aboRestTage(u: AboUser | null | undefined, jetzt: Date = new Date()): number {
  const m = u && u.aboEndetAm ? /^(\d{4})-(\d{2})-(\d{2})/.exec(String(u.aboEndetAm)) : null;
  if (!m) return 0;
  const ende = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const heute = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  return Math.max(0, Math.round((ende.getTime() - heute.getTime()) / MS_PER_DAY));
}

/**
 * Prüft ob ein User sich in der kostenlosen Probezeit befindet: kein Abo
 * aktiv, noch NIE ein Abo ({@link hatteSchonAbo}), Start + 3 Monate in der
 * Zukunft. Gleiche Regel wie `in_probezeit` in der Datenbank (Migration 145).
 */
export function inProbezeit(u: AboUser | null | undefined): boolean {
  if (!u || u.aboAktiv === true || hatAbo(u) || hatteSchonAbo(u)) return false;
  const datum = u.probeStart || u.createdAt || u.registriertAm;
  if (!datum) return false;
  const ende = new Date(datum);
  ende.setMonth(ende.getMonth() + PROBE_MONATE);
  return new Date() < ende;
}

/**
 * Berechnet die verbleibenden Tage in der kostenlosen Probezeit.
 *
 * @returns Verbleibende Probetage, 0 wenn abgelaufen.
 */
export function restProbetage(u: AboUser | null | undefined): number {
  if (!u) return 0;
  const datum = u.probeStart || u.createdAt || u.registriertAm;
  if (!datum) return 0;
  const ende = new Date(datum);
  ende.setMonth(ende.getMonth() + PROBE_MONATE);
  const diffMs = ende.getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / MS_PER_DAY));
}

/**
 * Prüft, ob ein User auf den Marktplatz zugreifen darf.
 */
export function hatMarktplatzZugang(user: AboUser | null | undefined): boolean {
  if (!user || user.kycStatus !== 'approved') return false;
  return hatAbo(user) || inProbezeit(user);
}

/**
 * Addiert Monate wie PostgreSQL (`+ interval 'n months'`): Gibt es den Tag im
 * Zielmonat nicht, wird auf den Monatsletzten gekürzt (31.05. − 3 Monate =
 * 28./29.02., nicht 03.03. wie bei `Date.setMonth`). Wichtig, weil die
 * Datenbank das Ende der Gratisphase so rechnet (`has_marktplatz_zugang_fuer`).
 */
function plusMonateWieDb(d: Date, monate: number): Date {
  const tag = d.getDate();
  const r = new Date(d.getFullYear(), d.getMonth() + monate, 1, d.getHours(), d.getMinutes(), d.getSeconds());
  const letzter = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(tag, letzter));
  return r;
}

/**
 * Ende der Gratisphase so, wie die Datenbank es rechnet: Start (`probeStart`,
 * sonst Registrierung) + {@link PROBE_MONATE}, Monatsende gekürzt.
 *
 * @returns Ende oder null, wenn kein Datum vorliegt
 */
export function probeEndeWieDb(u: AboUser | null | undefined): Date | null {
  const datum = u ? u.probeStart || u.createdAt || u.registriertAm : null;
  if (!datum) return null;
  const ende = plusMonateWieDb(new Date(datum), PROBE_MONATE);
  return isNaN(ende.getTime()) ? null : ende;
}

/**
 * Gratisphase soll an einem bestimmten Tag enden (Admin, 17.10.2026).
 *
 * Gespeichert ist nur der START (`probeStart`); das Ende ist immer Start +
 * {@link PROBE_MONATE} (so rechnet auch die Datenbank). Der Start wird deshalb
 * aus dem gewünschten Ende zurückgerechnet — wegen der Monatsenden so lange
 * einen Tag später, bis Start + Monate wirklich mindestens das Wunschende
 * ergibt. Ein Ende in der Vergangenheit beendet die Gratisphase.
 *
 * @param wunsch Gewünschtes Ende (Tag zählt, Uhrzeit egal)
 * @returns neuer Start (zum Speichern) und das Ende, das die Datenbank
 *   daraus rechnet (für die Anzeige). Es kann wegen kurzer Monate bis zu
 *   drei Tage über dem Wunsch liegen (31.12. ist mit 3 Monaten nicht genau
 *   erreichbar → 01.01.), nie darunter.
 */
export function probeStartFuerEnde(wunsch: Date): { start: Date; ende: Date } {
  const tag = new Date(wunsch.getFullYear(), wunsch.getMonth(), wunsch.getDate());
  let start = plusMonateWieDb(tag, -PROBE_MONATE);
  while (plusMonateWieDb(start, PROBE_MONATE) < tag) {
    start = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
  }
  return { start, ende: plusMonateWieDb(start, PROBE_MONATE) };
}

/**
 * „Gratisphase um X Tage verlängern" (Admin, 17.10.2026). Rechnet vom ENDE:
 *   - Gratisphase läuft noch → neues Ende = bisheriges Ende + X Tage
 *   - schon abgelaufen        → neues Ende = heute + X Tage
 *
 * Vorher wurde der Start um X Tage verschoben — ohne eingetragenen Start
 * (Rückfall auf das Registrierungsdatum) ab HEUTE: Aus „+30 Tage" wurden so
 * rund 122 Tage (Fall Tomica, 17.10.2026). Bei einem lange zurückliegenden
 * Start blieb das Ende dagegen in der Vergangenheit.
 *
 * @param u     Konto (probeStart / createdAt / registriertAm)
 * @param tage  Zahl der Tage, um die verlängert wird
 * @param jetzt Bezugszeitpunkt (für Tests)
 * @returns siehe {@link probeStartFuerEnde}
 */
export function probeVerlaengerung(
  u: AboUser | null | undefined,
  tage: number,
  jetzt: Date = new Date(),
): { start: Date; ende: Date } {
  const bisher = probeEndeWieDb(u);
  const basis = bisher && bisher > jetzt ? bisher : jetzt;
  return probeStartFuerEnde(new Date(basis.getFullYear(), basis.getMonth(), basis.getDate() + tage));
}
